export interface SelectIntentRequest {
  intent: 'create_project' | 'wait_for_invite';
}

export interface SelectIntentResponse {
  success: boolean;
  redirect?: string;
  error?: string;
}

export interface SetupGithubResponse {
  install_url: string;
  github_connected: boolean;
}

export interface SkipOnboardingResponse {
  success: boolean;
  redirect?: string;
  error?: string;
}

/**
 * Submit user's onboarding intent (create project or wait for invite)
 */
export async function selectIntent(data: SelectIntentRequest): Promise<SelectIntentResponse> {
  const response = await fetch('/api/onboarding/intent', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to submit intent');
  }

  return response.json();
}

/**
 * Get GitHub setup status and install URL
 */
export async function getGithubSetup(): Promise<SetupGithubResponse> {
  const response = await fetch('/api/onboarding/setup-github', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to get GitHub setup status');
  }

  return response.json();
}

/**
 * Complete onboarding and redirect to projects
 */
export async function continueToProjects(): Promise<{ success: boolean; redirect?: string }> {
  const response = await fetch('/api/onboarding/continue-to-projects', {
    headers: {
      'Accept': 'application/json',
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to continue to projects');
  }

  return response.json();
}

/**
 * Skip onboarding entirely
 */
export async function skipOnboarding(): Promise<SkipOnboardingResponse> {
  const response = await fetch('/api/onboarding/skip', {
    headers: {
      'Accept': 'application/json',
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to skip onboarding');
  }

  return response.json();
}

/**
 * Mark onboarding as completed (called after first project creation)
 */
export async function completeOnboarding(): Promise<{ success: boolean; error?: string }> {
  const response = await fetch('/api/onboarding/complete', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to complete onboarding');
  }

  return response.json();
}
